import React from 'react';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getToolDisplayName } from '../../services/responseParser';
import { selectActiveTool } from '../../store/selectors';
import { TableWrapper } from './TableWidget';

interface Props {
  body: string;
  isStreaming: boolean;
  isThinking: boolean;
  isError: boolean;
  hasMore: boolean;
  thinkingText?: string;
  onAskMore: () => void;
  onRetry?: () => void;
}

function ActiveToolIndicator() {
  const activeTool = useSelector(selectActiveTool);
  if (!activeTool) return null;
  return (
    <div className="rau-tool-indicator">
      <span className="rau-tool-indicator-spinner" />
      {getToolDisplayName(activeTool)}
    </div>
  );
}

function ThinkingToolLabel() {
  const activeTool = useSelector(selectActiveTool);
  return (
    <span className="rau-thinking-text">
      {activeTool ? getToolDisplayName(activeTool) : 'Thinking…'}
    </span>
  );
}

export function AssistantMessageBubble({
  body,
  isStreaming,
  isThinking,
  isError,
  hasMore,
  thinkingText,
  onAskMore,
  onRetry
}: Props) {
  const bubbleClass = [
    'rau-bubble',
    'rau-bubble--assistant',
    isError ? 'rau-bubble--error' : ''
  ]
    .join(' ')
    .trim();

  const thinkingBlock = thinkingText ? (
    <p className="rau-thinking-content">{thinkingText}</p>
  ) : null;

  if (isThinking) {
    return (
      <div className={bubbleClass}>
        {thinkingBlock}
        <div className="rau-thinking" aria-label="AI is working">
          <span />
          <span />
          <span />
          <ThinkingToolLabel />
        </div>
      </div>
    );
  }

  return (
    <div className={bubbleClass}>
      {thinkingBlock}
      <div className="rau-markdown">
        {isStreaming && <ActiveToolIndicator />}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ children }) => (
              <TableWrapper
                onAskMore={onAskMore}
                hasMore={hasMore}
                onRetry={onRetry}
              >
                {children}
              </TableWrapper>
            )
          }}
        >
          {body}
        </ReactMarkdown>
        {isStreaming && <span className="rau-cursor" />}
      </div>
    </div>
  );
}
